import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NbThemeService} from '@nebular/theme';
import {SpatialCardVm} from '@theme/components/card-components/spatial-card/spatial-card.vm';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {takeUntil, map, debounceTime} from 'rxjs/operators';
import {GeospatialCenterCoordinate} from '../../../../../pages/geospatial-viewer/model/viewer-config';
import {VisualizationType} from '../../../../../pages/geospatial-viewer/model/visualization';
import {
    AssetLayers,
    GenerateLayerService,
    LayerSettings,
} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {MeasureMetricService} from '@theme/components/card-components/spatial-card/components/measure-metric/measure-metric.service';
import {SpatialCardConfigMetric, SpatialCardDataMetric, SpatialCardResponseMetric} from '../model/model-spatial-card';
import {SpatialEntityData} from '../../../../../pages/geospatial-viewer/model/api';
import {SpatialCardBaseComponent} from '../spatial-card-base/spatial-card-base.component';
import {FormBuilder, FormGroup} from '@angular/forms';
import {BucketDto, MetricDto, MetricGroupDto} from '../../../../../pages/geospatial-viewer/layers/metric-layer';

@Component({
    selector: 'ngx-spatial-card-metrics',
    templateUrl: './spatial-card-metrics.component.html',
    styleUrls: [
        './spatial-card-metrics.component.scss',
        '../spatial-card.component.scss',
        '../../../../styles/esri-overrides.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpatialCardMetricsComponent extends SpatialCardBaseComponent<SpatialCardConfigMetric> implements OnInit {
    // Override: default map title
    @Input() title: string = 'Geospatial overview: assets';
    // Map data source: map data (i.e., assets), initial location
    @Input() dataSource$: Observable<SpatialCardResponseMetric> = new BehaviorSubject(null);
    // Metric selection and options
    @Input() metricSelectEnabled: boolean = true;
    // Support for selecting categories
    @Input() selectedCategories?: BehaviorSubject<string[]>;
    // Events: change map location, metric, select feature
    @Output() onMetricChange: EventEmitter<{measure: MetricGroupDto; metric: MetricDto}> = new EventEmitter();

    // Map data store
    private cardResponse: BehaviorSubject<SpatialCardResponseMetric> = new BehaviorSubject(null);
    readonly cardResponse$: Observable<SpatialCardResponseMetric> = this.cardResponse.asObservable();
    public selectedMeasure: BehaviorSubject<MetricGroupDto> = new BehaviorSubject<MetricGroupDto>(null);
    public selectedMetric: BehaviorSubject<MetricDto> = new BehaviorSubject<MetricDto>(null);
    public selectedCategoriesForm: FormGroup;

    constructor(
        public nbThemeService: NbThemeService,
        public vm: SpatialCardVm,
        public generateLayerService: GenerateLayerService,
        private metricService: MeasureMetricService,
        private fb: FormBuilder,
    ) {
        super(nbThemeService, vm, generateLayerService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.layerIdAlias = 'asset';
        if (!!this.selectedCategories) {
            this.initForm();
        }
    }

    /*
     * Implement abstract methods
     */
    public subscribeToMapChanges(): void {
        combineLatest([this.dataSource$, this.nbThemeService.onThemeChange()])
            .pipe(
                takeUntil(this.unsubscribe$),
                map(([resp, themeChange]: [SpatialCardResponseMetric, any]) => {
                    this.updateMapServices(resp, themeChange);
                    this.cardResponse.next(resp);

                    if (!this.mapInitializedFlag.value) {
                        super.initializeMapSequence(resp.configData);
                    }
                    this.updateMetricLayers(resp.sourceData, themeChange);
                }),
            )
            .subscribe();
    }

    public onSelectMapFeature(firstGraphic: any, _: any[]): void {
        if (firstGraphic == null) return;
        const attributes = firstGraphic.attributes;
        this.onMapFeatureSelected.emit({assetId: attributes.entityId, assetClassCode: attributes.entityType});
    }

    public onMapViewportChange(newCoordinate: GeospatialCenterCoordinate): void {
        const config: SpatialCardConfigMetric = this.cardResponse.getValue().configData;
        const newConfig: SpatialCardConfigMetric = {
            ...config,
            centerCoordinate: {
                ...config.centerCoordinate,
                ...newCoordinate,
            },
        };

        if (!this.vm.isEquivalent(config.centerCoordinate, newConfig.centerCoordinate)) {
            this.onMapLocationChange.emit(newConfig);
        }
    }

    /*
     * Custom methods
     */
    /** Update map services: legend, active metric, etc. */
    private updateMapServices(resp: SpatialCardResponseMetric, themeChange: any): void {
        // Update active metric, which controls the buckets in the legend
        this.legendBuckets.next(
            this.getMetricFromType(resp.sourceData.metricType, resp.configData.metricGroups)?.buckets || [],
        );
    }

    /** Helper for rendering the map layer(s). Remove existing layers, generate and add new layers from mapDataSource. */
    private updateMetricLayers(mapDataSource: SpatialCardDataMetric, theme: string): void {
        super.removeMapLayers();

        const mapData: SpatialEntityData[] = mapDataSource.data;
        const visualizationType: VisualizationType = mapDataSource.visualizationType;

        const activeMetric: MetricDto = this.getMetricFromType(mapDataSource.metricType);
        const activeMeasure: MetricGroupDto = this.getMeasureByMetricType(mapDataSource.metricType);

        // Render different map layers based on visualization type
        const popupSettings: LayerSettings = {
            showTooltip: true, // show tooltip
            suppressTooltipTitle: true, // hide the entity Id
            // inject this metric
            metrics: [activeMetric.key],
            // TODO: this inadvertently will disable the highlighting for overlapping assets. Need to refactor to merge strategy.
            showStaticLabel: false,
            staticLabelProps: {},
        };
        switch (visualizationType) {
            case VisualizationType.INDIVIDUAL:
                const assetLayers: AssetLayers = this.generateLayerService.createAssetFeatureLayers(
                    mapData,
                    visualizationType,
                    theme,
                    popupSettings,
                );
                this.map.addMany(assetLayers.layers);
                break;
            default:
                throw new Error('Invalid visualization type selected: ' + visualizationType);
        }
    }

    /** Get Metric based on given MetricType */
    private getMetricFromType(metricType: string, metricGroups: MetricGroupDto[] = this.getMetricGroups()): MetricDto {
        return this.metricService.getMetricFromMetricKey(metricType, metricGroups);
    }

    /** Get Measure (parent) based on given MetricType */
    private getMeasureByMetricType(
        metricType: string,
        metricGroups: MetricGroupDto[] = this.getMetricGroups(),
    ): MetricGroupDto {
        return this.metricService.getMeasureByMetricKey(metricType, metricGroups);
    }

    /** Get measures (aka, metric groups) from config */
    private getMetricGroups(): MetricGroupDto[] {
        return this.cardResponse.getValue()?.configData.metricGroups || [];
    }

    /** Support for change of metric on map component */
    public onUpdateMetric(event: {measure: MetricGroupDto; metric: MetricDto}): void {
        this.selectedMeasure.next(event.measure);
        this.selectedMetric.next(event.metric);
        this.onMetricChange.emit(event);
    }

    /** Initialize category form and subscription to form changes */
    private initForm() {
        this.selectedCategoriesForm = this.fb.group({
            categoriesSelected: [null],
        });

        this.selectedCategoriesForm
            .get('categoriesSelected')
            .valueChanges.pipe(
                debounceTime(500),
                map((categories: BucketDto[]) =>
                    categories.map((bucket) => {
                        return bucket.key;
                    }),
                ),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((selectedCategories: string[]) => this.selectedCategories.next(selectedCategories));
    }
}
